import { useEffect, useRef, useState } from 'react';

// Global event emitter for popup state management
class PopupEventEmitter extends EventTarget {
  private static instance: PopupEventEmitter;

  private constructor() {
    super();
  }

  static getInstance(): PopupEventEmitter {
    if (!PopupEventEmitter.instance) {
      PopupEventEmitter.instance = new PopupEventEmitter();
    }
    return PopupEventEmitter.instance;
  }

  openPopup(id: string) {
    this.dispatchEvent(new CustomEvent('popup-open', { detail: { id } }));
  }

  closeAll() {
    this.dispatchEvent(new Event('close-all'));
  }
}

export function usePopupManager(popupId: string) {
  const [isOpen, setIsOpen] = useState(false);
  const idRef = useRef(popupId);

  useEffect(() => {
    const emitter = PopupEventEmitter.getInstance();

    const handlePopupOpen = (event: CustomEvent) => {
      // Close this popup if another one is being opened
      if (event.detail.id !== idRef.current) {
        setIsOpen(false);
      }
    };

    const handleCloseAll = () => {
      setIsOpen(false);
    };

    emitter.addEventListener('popup-open', handlePopupOpen as EventListener);
    emitter.addEventListener('close-all', handleCloseAll);

    return () => {
      emitter.removeEventListener('popup-open', handlePopupOpen as EventListener);
      emitter.removeEventListener('close-all', handleCloseAll);
    };
  }, []);

  const openPopup = () => {
    const emitter = PopupEventEmitter.getInstance();
    emitter.openPopup(popupId);
    setIsOpen(true);
  };

  const closePopup = () => {
    setIsOpen(false);
  };

  return { isOpen, openPopup, closePopup };
}
